import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { dashboardService, PullbackBacktestPayload } from '../../services/DashboardService';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './PullbackStrategy.module.css';

const PERIOD_LABELS: Record<string, string> = {
  full: 'Full Period',
  in_sample: 'In-Sample',
  out_of_sample: 'Out-of-Sample',
};

const PullbackStrategy: React.FC = () => {
  const [data, setData] = useState<PullbackBacktestPayload | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('full');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const d = await dashboardService.getPullbackBacktest();
      setData(d);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load pullback data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fmt = (val: number, decimals = 2) =>
    (val || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const pnlClass = (val: number) => (val > 0 ? styles.positive : val < 0 ? styles.negative : '');

  if (loading) {
    return (
      <section className={styles.page}>
        <SignalLoader message="Loading pullback strategy data..." fullPage />
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className={styles.page}>
        <p className={styles.error}>{error || 'No pullback data available yet'}</p>
        <Link to="/home" className={styles.back}>
          &larr; Back to Home
        </Link>
      </section>
    );
  }

  const periodKeys = Object.keys(data.backtest || {});
  const activePeriodData = (data.backtest as any)?.[selectedPeriod] || {};
  const metrics = activePeriodData.metrics || {};
  const equityCurve = activePeriodData.equity_curve || [];
  const trades = activePeriodData.trades || [];

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Pullback Reversion Strategy</h1>
        <span className={styles.subtitle}>Mean-reversion on SPY pullbacks, 3-day hold period</span>
      </div>

      {periodKeys.length > 0 && (
        <div className={styles.periodSelector}>
          {periodKeys.map((key) => (
            <button
              key={key}
              className={`${styles.periodTab} ${selectedPeriod === key ? styles.periodTabActive : ''}`}
              onClick={() => setSelectedPeriod(key)}
            >
              {PERIOD_LABELS[key] || key}
            </button>
          ))}
        </div>
      )}

      {Object.keys(metrics).length > 0 ? (
        <div className={styles.metricsGrid}>
          {metrics.win_rate != null && (
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Win Rate</span>
              <span className={styles.metricValue}>{fmt(metrics.win_rate * 100, 1)}%</span>
            </div>
          )}
          {metrics.total_pnl != null && (
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Total P&L</span>
              <span className={`${styles.metricValue} ${pnlClass(metrics.total_pnl)}`}>${fmt(metrics.total_pnl)}</span>
            </div>
          )}
          {metrics.trades != null && (
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Trades</span>
              <span className={styles.metricValue}>{metrics.trades}</span>
            </div>
          )}
          {metrics.sharpe != null && (
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Sharpe</span>
              <span className={styles.metricValue}>{fmt(metrics.sharpe, 2)}</span>
            </div>
          )}
          {metrics.profit_factor != null && (
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Profit Factor</span>
              <span className={styles.metricValue}>{fmt(metrics.profit_factor, 2)}</span>
            </div>
          )}
          {metrics.max_drawdown != null && (
            <div className={styles.metricCard}>
              <span className={styles.metricLabel}>Max Drawdown</span>
              <span className={`${styles.metricValue} ${styles.negative}`}>{fmt(metrics.max_drawdown * 100, 1)}%</span>
            </div>
          )}
        </div>
      ) : (
        <p className={styles.empty}>
          No backtest metrics available yet. The sync script will populate this data once the backtest CSV is generated.
        </p>
      )}

      {equityCurve.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>Backtest Equity Curve</h2>
          <div className={styles.chartWrap}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={equityCurve}>
                <defs>
                  <linearGradient id="pbGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-purple)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="var(--color-purple)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  formatter={(v: number) => [`$${fmt(v)}`, 'Equity']}
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-primary)' }}
                />
                <Area type="monotone" dataKey="equity" stroke="var(--color-purple)" fill="url(#pbGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {trades.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>Backtest Trades ({trades.length})</h2>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Entry Date</th>
                  <th>Exit Date</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>Score</th>
                  <th>Hold</th>
                  <th>P&L</th>
                  <th>P&L %</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t: any, i: number) => (
                  <tr key={i}>
                    <td>{t.entry_date}</td>
                    <td>{t.exit_date}</td>
                    <td>${fmt(t.entry_price)}</td>
                    <td>${fmt(t.exit_price)}</td>
                    <td>{fmt(t.score, 1)}</td>
                    <td>{t.hold_days}d</td>
                    <td className={pnlClass(t.pnl)}>${fmt(t.pnl)}</td>
                    <td className={pnlClass(t.pnl_pct)}>{fmt(t.pnl_pct, 2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Link to="/home" className={styles.back}>
        &larr; Back to Home
      </Link>
    </section>
  );
};

export default PullbackStrategy;
